import type { ReactNode } from 'react'

/**
 * Shared page header for interior pages. Matches Home's container width
 * (`max-w-7xl`) so the left edge of the eyebrow/title/lede aligns flush
 * with the body sections that follow on the same page.
 */
export function PageHeader({
  eyebrow,
  title,
  lede,
  children,
}: {
  eyebrow?: string
  title: string
  lede?: string
  children?: ReactNode
}) {
  return (
    <section className="relative bg-primary text-white">
      <div className="h-1 bg-vice-sunset" aria-hidden="true" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        {eyebrow && (
          <div className="text-sm font-semibold uppercase tracking-wider text-accent-sunset">
            {eyebrow}
          </div>
        )}
        <h1 className="mt-2 text-4xl sm:text-5xl font-bold tracking-tight max-w-4xl">{title}</h1>
        {lede && (
          <p className="mt-4 text-lg text-neutral-200 leading-relaxed max-w-3xl">
            {lede}
          </p>
        )}
        {children}
      </div>
    </section>
  )
}

/**
 * Long-form content wrapper. Outer container matches Home / PageHeader so
 * the left edge stays flush. Internal text column is held at ~70ch for
 * readability but stays left-aligned (no centered narrow column).
 */
export function Prose({ children }: { children: ReactNode }) {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
      <div className="max-w-3xl prose-vice">{children}</div>
    </div>
  )
}
